import React, { useEffect } from 'react';
import { Bell, Shield, Key, FileWarning, Activity } from 'lucide-react';
import { Button } from '../ui/button';
import { NotificationsApi, NotificationSettings, PaginatedNotificationSettingsList } from 'api-client';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { useContext, useState } from 'react';
import { getApiConfiguration } from '@/api/utils';
import { toast } from 'sonner';

export const NotificationSettingsPage = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const client = new NotificationsApi(getApiConfiguration(token));
  const [settings, setSettings] = useState<NotificationSettings | null>(null);

  const getNotificationSettings = async () => {
    try {
      if (!token || !currentTeam?.slug) return;
      const response = await client.notificationsSettingsRetrieve({ id: currentTeam.slug, teamSlug: currentTeam.slug });
      setSettings(response);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      toast.error('Failed to fetch notification settings');
    }
  };

  useEffect(() => {
    getNotificationSettings();
  }, [token, currentTeam?.slug]);

  const handleSubmit = async () => {
    if (!token || !currentTeam?.slug) return;
    const response = await client.notificationsSettingsUpdate({
      id: currentTeam.slug,
      teamSlug: currentTeam.slug,
      notificationSettings: settings || undefined
    });
    setSettings(response);
    toast.success('Notification settings updated successfully');
  };

  // Helper function to handle setting changes in a type-safe way
  const handleSettingChange = (key: keyof Omit<NotificationSettings, 'teamSlug'>, value: boolean) => {
    if (!settings) return;
    
    // Create a proper copy with all required fields
    const updatedSettings = {
      ...settings,
      [key]: value
    };
    
    setSettings(updatedSettings);
  };

  const onReset = () => {
    if (!settings) return;
    
    // Create a new settings object with all notification properties set to true
    const updatedSettings = { ...settings };
    
    // Type-safe way to update specific notification properties
    if ('notifyFindings' in updatedSettings) updatedSettings.notifyFindings = true;
    if ('notifyDomains' in updatedSettings) updatedSettings.notifyDomains = true;
    if ('notifySubdomains' in updatedSettings) updatedSettings.notifySubdomains = true;
    if ('notifyIpAddresses' in updatedSettings) updatedSettings.notifyIpAddresses = true;
    if ('notifyPorts' in updatedSettings) updatedSettings.notifyPorts = true;
    if ('notifyCriticalFindings' in updatedSettings) updatedSettings.notifyCriticalFindings = true;
    if ('notifyHighFindings' in updatedSettings) updatedSettings.notifyHighFindings = true;
    if ('notifyMediumFindings' in updatedSettings) updatedSettings.notifyMediumFindings = true;
    if ('notifyLowFindings' in updatedSettings) updatedSettings.notifyLowFindings = true;
    if ('notifyInfoFindings' in updatedSettings) updatedSettings.notifyInfoFindings = true;
    if ('notifyRiskFindings' in updatedSettings) updatedSettings.notifyRiskFindings = true;
    if ('notifyDataLeaks' in updatedSettings) updatedSettings.notifyDataLeaks = true;
    if ('notifyStealerLeaks' in updatedSettings) updatedSettings.notifyStealerLeaks = true;
    if ('notifyPublicLeaks' in updatedSettings) updatedSettings.notifyPublicLeaks = true;
    
    setSettings(updatedSettings);
    handleSubmit();
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Bell className="h-5 w-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-white">Notification Settings</h2>
        </div>

        <div className="space-y-6">
          {/* Asset Alerts */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-medium text-white">Asset Alerts</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Domains Updates</div>
                  <div className="text-sm text-gray-400">Get notified when critical vulnerabilities are detected</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input 
                      checked={settings?.notifyDomains} 
                      onChange={(e) => handleSettingChange('notifyDomains', e.target.checked)} 
                      type="checkbox" 
                      className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" 
                    />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">SubDomains Updates</div>
                  <div className="text-sm text-gray-400">Get notified about high-risk security findings</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifySubdomains} onChange={(e) => handleSettingChange('notifySubdomains', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Ports Updates</div>
                  <div className="text-sm text-gray-400">Get notified about ports updates</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyPorts} onChange={(e) => handleSettingChange('notifyPorts', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Ip Addresses Updates</div>
                  <div className="text-sm text-gray-400">Get notified about ip addresses updates</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyIpAddresses} onChange={(e) => handleSettingChange('notifyIpAddresses', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          {/* Security Alerts */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-medium text-white">Security Alerts</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Critical Vulnerabilities</div>
                  <div className="text-sm text-gray-400">Get notified when critical vulnerabilities are detected</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyCriticalFindings} onChange={(e) => handleSettingChange('notifyCriticalFindings', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">High Risk Findings</div>
                  <div className="text-sm text-gray-400">Get notified about high-risk security findings</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyHighFindings} onChange={(e) => handleSettingChange('notifyHighFindings', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Medium Risk Findings</div>
                  <div className="text-sm text-gray-400">Get notified about medium-risk security findings</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyMediumFindings} onChange={(e) => handleSettingChange('notifyMediumFindings', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Low Risk Findings</div>
                  <div className="text-sm text-gray-400">Get notified about low-risk security findings</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyLowFindings} onChange={(e) => handleSettingChange('notifyLowFindings', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Info Findings</div>
                  <div className="text-sm text-gray-400">Get notified about info security findings</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyInfoFindings} onChange={(e) => handleSettingChange('notifyInfoFindings', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Risk Findings</div>
                  <div className="text-sm text-gray-400">Get notified about risk security findings</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyFindings} onChange={(e) => handleSettingChange('notifyFindings', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Data Leaks</div>
                  <div className="text-sm text-gray-400">Get notified about data security findings</div>
                </div>
                <div className="flex items-center space-x-4">
                  {/* <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label> */}
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyDataLeaks} onChange={(e) => handleSettingChange('notifyDataLeaks', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Credential Leaks */}
          <div className="space-y-4 pt-6 border-t border-gray-800">
            <div className="flex items-center space-x-2">
              <Key className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-medium text-white">Credential Leaks</h3>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
              <div className="space-y-1">
                <div className="text-sm font-medium text-white">Stealer Leaks</div>
                <div className="text-sm text-gray-400">Get notified when credentials are found in data breaches</div>
              </div>
              <div className="flex items-center space-x-4">
                {/* <label className="flex items-center space-x-2">
                  <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                  <span className="text-sm text-gray-300">Email</span>
                </label> */}
                <label className="flex items-center space-x-2">
                  <input checked={settings?.notifyStealerLeaks} onChange={(e) => handleSettingChange('notifyStealerLeaks', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                  <span className="text-sm text-gray-300">Push</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
              <div className="space-y-1">
                <div className="text-sm font-medium text-white">Public Leaks</div>
                <div className="text-sm text-gray-400">Get notified when credentials are found in data breaches</div>
              </div>
              <div className="flex items-center space-x-4">
                {/* <label className="flex items-center space-x-2">
                  <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                  <span className="text-sm text-gray-300">Email</span>
                </label> */}
                <label className="flex items-center space-x-2">
                  <input checked={settings?.notifyPublicLeaks} onChange={(e) => handleSettingChange('notifyPublicLeaks', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                  <span className="text-sm text-gray-300">Push</span>
                </label>
              </div>
            </div>
          </div>

          {/* System Notifications */}
          {/* <div className="space-y-4 pt-6 border-t border-gray-800">
            <div className="flex items-center space-x-2">
              <Activity className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-medium text-white">System Notifications</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Scan Completion</div>
                  <div className="text-sm text-gray-400">Get notified when security scans are completed</div>
                </div>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyScanCompletion} onChange={(e) => handleSettingChange('notifyScanCompletion', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-white">Weekly Reports</div>
                  <div className="text-sm text-gray-400">Receive weekly security summary reports</div>
                </div>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyWeeklyReport} onChange={(e) => handleSettingChange('notifyWeeklyReport', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Email</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input checked={settings?.notifyWeeklyReport} onChange={(e) => handleSettingChange('notifyWeeklyReport', e.target.checked)} type="checkbox" className="h-4 w-4 rounded border-gray-700 bg-gray-800/30 text-blue-500 focus:ring-blue-500/50" />
                    <span className="text-sm text-gray-300">Push</span>
                  </label>
                </div>
              </div>
            </div>
          </div> */}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-800">
          <Button variant="outline" onClick={onReset}>Reset to Default</Button>
          <Button onClick={handleSubmit}>Save Changes</Button>
        </div>
      </div>
    </div>
  );
};